import { TaskDataUpdates } from '@cibo/core'
import { useSaveIndicator } from '@cibo/ui'
import { useSnackbar } from 'notistack'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../../context'
import { useUpdateEngagementTaskData } from '../../../../../queries/useUpdateEngagementTaskData'
import { DataEntrySchemaEditingContext } from './DataEntrySchemaEditingContext'
import { SchemaTraitDialog } from './SchemaTraitDialog'
import { DataEntrySchemaEditingState } from './types'

export const DataEntrySchemaEditingProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation()
  const [state, setState] = React.useState<DataEntrySchemaEditingState>({ open: false })

  const { enqueueSnackbar } = useSnackbar()
  const { setIsSaving, setIsError, setIsDirty } = useSaveIndicator()

  const { engagementId, taskId, _TEST_ONLY_overrideSave } = useProgramEngagementContext()

  const updateTaskData = useUpdateEngagementTaskData({
    engagementId,
    taskId,
  })

  const saveTaskData = useCallback(
    (updates: TaskDataUpdates, options?: { onError?: (error: Error) => void }) => {
      if (_TEST_ONLY_overrideSave) {
        _TEST_ONLY_overrideSave(updates)
        setState({ open: false })
        return Promise.resolve(undefined)
      }
      setIsError(false)
      setIsDirty(true)
      setIsSaving(true)
      return updateTaskData
        .mutateAsync({
          updates,
        })
        .then(response => {
          setIsSaving(false)
          setState({ open: false })
          return response
        })
        .catch(error => {
          setIsSaving(false)
          setIsError(true)
          enqueueSnackbar(t(`error_${error.response?.data.errMsgKey || 'generic'}`), {
            variant: 'error',
          })
          options?.onError?.(error)
          throw error
        })
    },
    []
  )

  return (
    <DataEntrySchemaEditingContext.Provider value={{ setState, saveTaskData }}>
      {children}

      <SchemaTraitDialog state={state} setState={setState} saveTaskData={saveTaskData} />
    </DataEntrySchemaEditingContext.Provider>
  )
}
